import React, { useState, useRef } from "react";
import { Box, Button } from "@material-ui/core";
import { GetApp, Clear } from "@material-ui/icons";
import { useTranslation } from "react-i18next";

interface DrawingCanvasProps {
  onDrawingComplete: (dataUrl: string) => void;
}

const DrawingCanvas: React.FC<DrawingCanvasProps> = ({ onDrawingComplete }) => {
  const { t } = useTranslation();
  const [isDrawing, setIsDrawing] = useState<boolean>(false);
  const [hasDrawing, setHasDrawing] = useState<boolean>(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    setIsDrawing(true);
    const rect = canvas.getBoundingClientRect();
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    setHasDrawing(true);

    // Convert canvas to base64 and notify parent
    const canvas = canvasRef.current;
    if (canvas) {
      const dataUrl = canvas.toDataURL("image/png");
      onDrawingComplete(dataUrl);
    }
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawing(false);
    onDrawingComplete("");
  };

  const handleSaveDrawing = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dataUrl = canvas.toDataURL("image/png");
    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = `drawing-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <Box display="flex" justifyContent="center" mb={1} border="1px solid #ccc" borderRadius={4} p={1} bgcolor="#fff">
        <canvas
          ref={canvasRef}
          width={400}
          height={300}
          style={{
            border: "1px solid #ddd",
            cursor: "crosshair",
            touchAction: "none",
          }}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
        />
      </Box>
      <Box display="flex" flexWrap="wrap">
        <Box mr={1} mb={1}>
          <Button variant="outlined" onClick={clearCanvas} startIcon={<Clear />}>
            {t("imageGenerator.clearDrawing")}
          </Button>
        </Box>
        {hasDrawing && (
          <Box mb={1}>
            <Button variant="outlined" onClick={handleSaveDrawing} startIcon={<GetApp />}>
              {t("imageGenerator.downloadDrawing")}
            </Button>
          </Box>
        )}
      </Box>
    </>
  );
};

export default DrawingCanvas;
